import { endOfMonth, format } from "date-fns";
import { find, union } from "lodash";
import {
  IFilterItem,
  IGenericAggregateResponse,
} from "../generic/generic-types";
import { maxSelectedClients } from "../utils/clientlistHelper";
import { Format } from "../utils/format";
import {
  IAbacusFilter,
  IClientAccount,
  IClientAccountFromList,
} from "../utils/types";

const isoDateFormat = "YYYY-MM-DD";

type portfolioFilter =
  | string
  | string[]
  | IClientAccount[]
  | IClientAccountFromList[];

// tslint:disable-next-line:cognitive-complexity
export function BuildPortfolioFilters(
  filterProperties:
    | {
        [attribute: string]: portfolioFilter;
      }
    | undefined
): IFilterItem[] {
  const filters: IFilterItem[] = [];
  const covredClientAttribute = "clientgen_id";

  if (filterProperties !== undefined) {
    for (const key of Object.keys(filterProperties)) {
      if (key === "columns" || key === "text") {
        continue;
      }
      if (
        filterProperties[key] !== undefined &&
        filterProperties[key] instanceof Array &&
        filterProperties[key].length > 0
      ) {
        if (key === "accounts" || key === "accountsList") {
          if (
            filters.length > 0 &&
            find(filters, { attribute: "clientgen_id" })
          ) {
            for (const filter of filters) {
              if (
                filter.attribute === covredClientAttribute &&
                filterProperties[key]
              ) {
                filter.value = `${filter.value}+${BuildAccountValues(
                  filterProperties[key],
                  key === "accountsList" ? true : false
                )}`;

                const splitted = filter.value.split("+");
                const uniques = union(splitted);
                const slicedStrings = uniques.slice(0, maxSelectedClients);

                filter.value = slicedStrings.join("+");
              }
            }
          } else {
            filters.push({
              attribute: covredClientAttribute,
              equals: true,
              value: BuildAccountValues(
                filterProperties[key],
                key === "accountsList" ? true : false
              ),
            });
          }
          continue;
        }
        filters.push({
          attribute: key,
          equals: true,
          value: BuildValues(filterProperties[key]),
        });
      }
      if (
        key === "bdrCommercialId" &&
        filterProperties[key] !== undefined &&
        filterProperties[key] !== ""
      ) {
        filters.push({
          attribute: covredClientAttribute,
          equals: true,
          value: filterProperties.bdrCommercialId as string,
        });
      }
    }
  }
  return filters;
}

export function BuildAbacusFilters(
  filterProperties:
    | {
        [attribute: string]: portfolioFilter;
      }
    | undefined
): IAbacusFilter {
  const filter: IAbacusFilter = { fpv: [], type: [], zone: [], client: "" };

  if (filterProperties !== undefined) {
    filter.client = filterProperties.bdrCommercialId
      ? (filterProperties.bdrCommercialId as string)
      : "";
    filter.startDate = filterProperties.value
      ? abacusDateFormat(filterProperties.value)
      : abacusDateFormat(new Date());
    filter.fpv = filterProperties.FpvSbuLiq
      ? (filterProperties.FpvSbuLiq as string[])
      : [];
    filter.type = filterProperties.ClientTypo
      ? (filterProperties.ClientTypo as string[])
      : [];
    filter.zone = filterProperties.ZoneGeo
      ? (filterProperties.ZoneGeo as string[])
      : [];
    filter.clientLevel = filterProperties.bdrCommercialLevel
      ? (filterProperties.bdrCommercialLevel as string)
      : "";
    filter.fpvLevel = filterProperties.level
      ? (filterProperties.level as string)
      : "0";
  }
  return filter;
}
function BuildValues(values: any[] | any) {
  if (values.length === 1) {
    return ReplaceInvalidCharacters(values[0]);
  }
  return ReplaceInvalidCharacters(values.join("+"));
}

// replaces , inside values with ~,
function ReplaceInvalidCharacters(valueString: string) {
  const re = /\,/gi;
  return valueString.replace(re, "~,");
}

function BuildAccountValues(
  values: portfolioFilter,
  accountList: boolean
): string {
  const accountFilter: string[] = [];
  if (accountList) {
    for (const account of values as IClientAccountFromList[]) {
      accountFilter.push(account.bdrId.bdrCommercialId.id);
    }
  } else {
    for (const account of values as IClientAccount[]) {
      accountFilter.push(account.bdrCommercialId);
    }
  }

  if (accountFilter.length === 1) {
    return accountFilter[0];
  }
  return accountFilter.join("+");
}

export function oldCreateDateFromFilter(
  filterProperties: { [attribute: string]: string[] | string } | undefined
) {
  const dateKey = "monthYearDate";
  if (
    filterProperties !== undefined &&
    filterProperties[dateKey] !== undefined &&
    filterProperties[dateKey].toString().length >= 7
  ) {
    const date = filterProperties[dateKey].toString();
    const newDate: Date = new Date(date);
    const year = newDate.getFullYear();
    const month = newDate.getMonth() + 1;
    return new Date(year, month, 1).toISOString().slice(0, 10);
  } else {
    const currentDate: Date = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    return new Date(year, month, 1).toISOString().slice(0, 10);
  }
}

type filterType =
  | { [attribute: string]: string[] | string | IClientAccount[] }
  | undefined;

export function GetPortfolioFilterAsofDate(filters: filterType) {
  if (filters && filters.value !== undefined) {
    return new Date(filters.value.toString()).toISOString().slice(0, 10);
  } else {
    return new Date().toISOString().slice(0, 10);
  }
}

export function CreateDateFromFilter(filterProperties: filterType) {
  const dateKey = "monthYearDate";
  const valueKey = "Date";
  if (
    filterProperties !== undefined &&
    filterProperties[dateKey] !== undefined &&
    filterProperties[dateKey].toString().length >= 7
  ) {
    const date = filterProperties[dateKey].toString();
    return format(
      endOfMonth(Format.ParseDateWithNamedMonths(date)),
      isoDateFormat
    );
  } else if (
    filterProperties !== undefined &&
    filterProperties[valueKey] !== undefined &&
    filterProperties[valueKey].length > 0 &&
    filterProperties[valueKey][0].toString().length >= 7
  ) {
    const date = filterProperties[valueKey][0].toString();
    return format(new Date(date), isoDateFormat);
  } else {
    return format(endOfMonth(new Date()), isoDateFormat);
  }
}

export function CreateDateFromFilterForAsOfDate(
  filterProperties: filterType
): string {
  const dateKey = "monthYearDate";
  if (
    filterProperties !== undefined &&
    filterProperties[dateKey] !== undefined &&
    filterProperties[dateKey].toString().length >= 7
  ) {
    const date = filterProperties[dateKey].toString();
    return format(Format.ParseDateWithNamedMonths(date), isoDateFormat);
  } else {
    return format(new Date(), isoDateFormat);
  }
}

export function ValidateGenericData(result: IGenericAggregateResponse) {
  if (
    result === undefined ||
    result.aggregates === undefined ||
    result.aggregates.length === 0
  ) {
    return false;
  }
  return true;
}

function abacusDateFormat(date: any) {
  const d =
    typeof date === "function"
      ? date()
      : typeof date === "string"
      ? new Date(date)
      : date;
  return d === undefined || d === "" || !(d instanceof Date)
    ? ""
    : `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}
